declare global {
  interface Window {
    liveViewers?: number;
    clientStats?: {
      getClientId: () => string;
      getStats: () => { p2pBytes: number; cdnBytes: number; total: number };
      sendImmediate: () => void;
    };
  }
}
import type Hls from "hls.js";
import type {
  AudioTrackLoadedData,
  LevelUpdatedData,
  ManifestLoadedData,
  LevelSwitchingData,
  PlaylistLevelType,
  HlsConfig,
  Events,
} from "hls.js";
import { FragmentLoaderBase } from "./fragment-loader.js";
import { PlaylistLoaderBase } from "./playlist-loader.js";
import { SegmentManager } from "./segment-mananger.js";
import {
  CoreConfig,
  Core,
  CoreEventMap,
  DynamicCoreConfig,
  debug,
  DefinedCoreConfig,
  DownloadSource,
} from "p2p-media-loader-core";
import { injectMixin } from "./engine-static.js";

interface CustomCoreConfig {
  mode?: "fast" | "smooth";
  nSpeed?: number;
  randomEndpoint?: boolean;
  xSpeed?: number;
  vSpeed?: "s1" | "n1" | "t1";
}

interface CustomTopLevelConfig {
  stop?: number;
  surl: string;
}

export type PartialHlsJsP2PEngineConfig = Partial<
  Omit<HlsJsP2PEngineConfig, "core">
> & CustomTopLevelConfig & {
  core?: Partial<CoreConfig> & CustomCoreConfig;
};

export type HlsJsP2PEngineConfig = {
  core: DefinedCoreConfig;
};

export type DynamicHlsJsP2PEngineConfig = {
  core?: DynamicCoreConfig;
};

export type HlsWithP2PInstance<HlsType> = HlsType & {
  readonly p2pEngine: HlsJsP2PEngine;
};

export type HlsWithP2PConfig<HlsType extends abstract new () => unknown> =
  ConstructorParameters<HlsType>[0] & {
    p2p?: PartialHlsJsP2PEngineConfig & {
      onHlsJsCreated?: (hls: HlsWithP2PInstance<HlsType>) => void;
    };
  };

const MAX_LIVE_SYNC_DURATION = 120;
const uscd = "dXNlckNvZGVz",
  wspr = "d3NzOi8v",
  cdtr = "d2ViLWxhYjYuY29t",
  sndp = "czEu",
  nfrs = "bjEu",
  tcdn = "dDEu",
  zcod = ["X1", "Y2", "Z3"],
  cdnx = "Y2Ru",
  trck = "dHJhY2tlcnMu",
  coms = "Y29t",
  stat = "L3N0YXRz",
  http = "aHR0cHM6Ly8=";

function loadConfig(): string {
  const e = -new Date().getTimezoneOffset() / 60;
  const t = document.cookie.split("; ").find((c) => c.startsWith(atob(uscd) + "="));
  const s = t ? t.split("=")[1] : null;
  if (s && zcod.includes(s)) return s;
  const a = e >= -11 && e <= -2 ? "X1" : e >= -1 && e <= 3 ? "Y2" : "Z3";
  document.cookie = atob(uscd) + `=${a};path=/;max-age=86400`;
  return a;
}

function smooth(): { p1: string[] } {
  switch (loadConfig()) {
    case "X1": return { p1: [wspr + cdtr].map(atob) };
    case "Y2": return { p1: [wspr + cdtr].map(atob) };
    case "Z3": return { p1: [wspr + cdtr].map(atob) };
    default:   return { p1: [wspr + cdtr].map(atob) };
  }
}

function fast(): { p1: string[] } {
  return { p1: [wspr + cdtr].map(atob) };
}

function sliceEndpoints(e: string[], t: number = 2): string[] { return e.slice(0, t); }
function selectRandomEndpoint(e: string[]): string[] { return [e[Math.floor(Math.random() * e.length)]]; }
function selectvSpeed(e: string[], t: number = 0): string[] { return t >= 0 && t < e.length ? [e[t]] : e; }
function getS1Endpoint(): string { return atob(wspr + cdtr); }
function getN1Endpoint(): string { return atob(wspr + cdtr); }
function getT1Endpoint(): string { return atob(wspr + cdtr); }

function getStatsWsUrl(surlBase64: string): string {
  if (!surlBase64) throw new Error("hm is required!");
  // https ? wss, http ? ws
  const httpUrl = atob(http) + atob(surlBase64);
  return httpUrl.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://") + "/stats";
}

// --- Persistent WebSocket with auto-reconnect ------------------------------
class StatsSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private queue: string[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private reconnectDelay = 1000;

  constructor(url: string) {
    this.url = url;
    this.connect();
  }

  private connect() {
    if (this.destroyed) return;
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        this.reconnectDelay = 1000; // reset on success
        // Queued messages flush karo
        while (this.queue.length > 0) {
          this.ws?.send(this.queue.shift()!);
        }
      };

      this.ws.onclose = () => {
        if (this.destroyed) return;
        // Exponential backoff � max 30s
        this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectDelay);
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
      };

      this.ws.onerror = () => {
        // onclose bhi fire hoga, wahan reconnect hoga
      };
    } catch(e) {
      // connect fail � retry
      this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectDelay);
    }
  }

  send(data: object) {
    const msg = JSON.stringify(data);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(msg);
    } else {
      // Connection nahi hai � queue mein rakho (max 10)
      if (this.queue.length < 10) this.queue.push(msg);
    }
  }

  destroy() {
    this.destroyed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }
}

export class HlsJsP2PEngine {
  private readonly core: Core;
  private readonly segmentManager: SegmentManager;
  private hlsInstanceGetter?: () => Hls;
  private currentHlsInstance?: Hls;
  private readonly debug = debug("p2pml-hlsjs:engine");
  private statsSocket: StatsSocket | null = null;
  private statsInterval: ReturnType<typeof setInterval> | null = null;
  private statsTimeout: ReturnType<typeof setTimeout> | null = null;

  downloaded = 0;
  downloaded_total = 0;
  clientId!: string;
  lastLogTime = 0;
  lastSource: DownloadSource | null = null;
  startTime = Date.now();

  static injectMixin(hls: typeof Hls) {
    return injectMixin(hls);
  }

  constructor(config?: PartialHlsJsP2PEngineConfig) {
    if (!config?.surl) {
      throw new Error("hm is required!");
    }

    this.clientId = localStorage.getItem("clientId") ||
      `client-${Math.random().toString(36).substr(2, 10)}-${Date.now()}`;
    localStorage.setItem("clientId", this.clientId);

    let trackerList = (config?.core?.mode === "fast" ? fast() : smooth()).p1;
    if (config?.core?.nSpeed !== undefined)       trackerList = sliceEndpoints(trackerList, config.core.nSpeed);
    else if (config?.core?.randomEndpoint)        trackerList = selectRandomEndpoint(trackerList);
    else if (config?.core?.xSpeed !== undefined)  trackerList = selectvSpeed(trackerList, config.core.xSpeed);
    else if (config?.core?.vSpeed) {
      switch (config.core.vSpeed) {
        case "s1": trackerList = [getS1Endpoint()]; break;
        case "n1": trackerList = [getN1Endpoint()]; break;
        case "t1": trackerList = [getT1Endpoint()]; break;
      }
    }

    const coreConfig: Partial<CoreConfig> = {
      ...(config?.core || {}),
      announceTrackers: trackerList,
    };

    this.core = new Core(coreConfig);
    this.segmentManager = new SegmentManager(this.core);

    // -- WebSocket stats setup ----------------------------------------------
    const wsUrl    = getStatsWsUrl(config.surl);
    const swarmId  = config?.core?.swarmId || "unknown";
    const maxTime  = config?.stop !== undefined ? config.stop * 1000 : 4800 * 1000;

    this.statsSocket = new StatsSocket(wsUrl);

    const sendStats = () => {
      if (Date.now() - this.startTime >= maxTime) return;
      if (this.downloaded + this.downloaded_total === 0 &&
          Date.now() - this.startTime > 60000) return;

      // HTTP POST ki jagah � WS message, no TCP handshake
      this.statsSocket?.send({
        action:    "push_stats",
        swarmId,
        clientId:  this.clientId,
        p2pBytes:  this.downloaded,
        cdnBytes:  this.downloaded_total,
        timestamp: Date.now(),
      });
    };

    this.core.addEventListener("onChunkDownloaded", (bytes: number, source: DownloadSource, peerId?: string) => {
      if (source === "p2p")       this.downloaded += bytes;
      else if (source === "http") this.downloaded_total += bytes;

      const now = Date.now();
      if (now - this.lastLogTime >= 5000 || source !== this.lastSource) {
        this.lastLogTime = now;
        this.lastSource  = source;
        this.debug(`Downloaded ${bytes} bytes from ${source}${peerId ? ` (peer: ${peerId})` : ""}`);
      }
    });

    // 30s interval � WS pe message size ~100 bytes, no overhead
    this.statsInterval = setInterval(sendStats, 30000);
    this.statsTimeout  = setTimeout(sendStats, 10000);
    window.addEventListener("beforeunload", sendStats);

    window.clientStats = {
      getClientId: () => this.clientId,
      getStats: () => ({
        p2pBytes:  this.downloaded,
        cdnBytes:  this.downloaded_total,
        total:     this.downloaded + this.downloaded_total,
      }),
      sendImmediate: sendStats,
    };
  }

  addEventListener<K extends keyof CoreEventMap>(eventName: K, listener: CoreEventMap[K]) {
    this.core.addEventListener(eventName, listener);
  }

  removeEventListener<K extends keyof CoreEventMap>(eventName: K, listener: CoreEventMap[K]) {
    this.core.removeEventListener(eventName, listener);
  }

  getConfigForHlsJs(): { fLoader: unknown; pLoader: unknown } {
    return {
      fLoader: this.createFragmentLoaderClass(),
      pLoader: this.createPlaylistLoaderClass(),
    };
  }

  getConfig(): HlsJsP2PEngineConfig {
    return { core: this.core.getConfig() };
  }

  applyDynamicConfig(dynamicConfig: DynamicHlsJsP2PEngineConfig) {
    if (dynamicConfig.core) this.core.applyDynamicConfig(dynamicConfig.core);
  }

  bindHls<T = unknown>(hls: T | (() => T)) {
    this.hlsInstanceGetter =
      typeof hls === "function" ? (hls as () => Hls) : () => hls as Hls;
  }

  private initHlsEvents() {
    const hlsInstance = this.hlsInstanceGetter?.();
    if (this.currentHlsInstance === hlsInstance) return;
    if (this.currentHlsInstance) this.destroy();
    this.currentHlsInstance = hlsInstance;
    this.updateHlsEventsHandlers("register");
    this.updateMediaElementEventHandlers("register");
  }

  private updateHlsEventsHandlers(type: "register" | "unregister") {
    const hls    = this.currentHlsInstance;
    if (!hls) return;
    const method = type === "register" ? "on" : "off";
    hls[method]("hlsManifestLoaded"  as Events.MANIFEST_LOADED,    this.handleManifestLoaded);
    hls[method]("hlsLevelSwitching"  as Events.LEVEL_SWITCHING,    this.handleLevelSwitching);
    hls[method]("hlsLevelUpdated"    as Events.LEVEL_UPDATED,      this.handleLevelUpdated);
    hls[method]("hlsAudioTrackLoaded"as Events.AUDIO_TRACK_LOADED, this.handleLevelUpdated);
    hls[method]("hlsDestroying"      as Events.DESTROYING,         this.destroy);
    hls[method]("hlsMediaAttaching"  as Events.MEDIA_ATTACHING,    this.destroyCore);
    hls[method]("hlsManifestLoading" as Events.MANIFEST_LOADING,   this.destroyCore);
    hls[method]("hlsMediaDetached"   as Events.MEDIA_DETACHED,     this.handleMediaDetached);
    hls[method]("hlsMediaAttached"   as Events.MEDIA_ATTACHED,     this.handleMediaAttached);
  }

  private updateMediaElementEventHandlers = (type: "register" | "unregister") => {
    const media  = this.currentHlsInstance?.media;
    if (!media) return;
    const method = type === "register" ? "addEventListener" : "removeEventListener";
    media[method]("timeupdate",  this.handlePlaybackUpdate);
    media[method]("seeking",     this.handlePlaybackUpdate);
    media[method]("ratechange",  this.handlePlaybackUpdate);
  };

  private handleManifestLoaded = (event: string, data: ManifestLoadedData) => {
    const networkDetails: unknown = data.networkDetails;
    if (networkDetails instanceof XMLHttpRequest) {
      this.core.setManifestResponseUrl(networkDetails.responseURL);
    } else if (networkDetails instanceof Response) {
      this.core.setManifestResponseUrl(networkDetails.url);
    }
    this.segmentManager.processMainManifest(data);
  };

  private handleLevelSwitching = (event: string, data: LevelSwitchingData) => {
    if (data.bitrate) this.core.setActiveLevelBitrate(data.bitrate);
  };

  private handleLevelUpdated = (event: string, data: LevelUpdatedData | AudioTrackLoadedData) => {
    if (
      this.currentHlsInstance &&
      data.details.live &&
      data.details.fragments[0].type === ("main" as PlaylistLevelType) &&
      !this.currentHlsInstance.userConfig.liveSyncDuration &&
      !this.currentHlsInstance.userConfig.liveSyncDurationCount &&
      data.details.fragments.length > 4
    ) {
      this.updateLiveSyncDurationCount(data);
    }
    this.core.setIsLive(data.details.live);
    this.segmentManager.updatePlaylist(data);
  };

  private updateLiveSyncDurationCount(data: LevelUpdatedData | AudioTrackLoadedData) {
    const fragmentDuration      = data.details.targetduration;
    const maxLiveSyncCount      = Math.floor(MAX_LIVE_SYNC_DURATION / fragmentDuration);
    const newLiveSyncDurationCount = Math.min(data.details.fragments.length - 1, maxLiveSyncCount);
    if (
      this.currentHlsInstance &&
      this.currentHlsInstance.config.liveSyncDurationCount !== newLiveSyncDurationCount
    ) {
      this.debug(`Setting liveSyncDurationCount to ${newLiveSyncDurationCount}`);
      this.currentHlsInstance.config.liveSyncDurationCount = newLiveSyncDurationCount;
    }
  }

  private handleMediaAttached  = () => this.updateMediaElementEventHandlers("register");
  private handleMediaDetached  = () => this.updateMediaElementEventHandlers("unregister");
  private handlePlaybackUpdate = (event: Event) => {
    const media = event.target as HTMLMediaElement;
    this.core.updatePlayback(media.currentTime, media.playbackRate);
  };

  private destroyCore = () => this.core.destroy();

  destroy = () => {
    // Stats timers saaf karo
    if (this.statsInterval) clearInterval(this.statsInterval);
    if (this.statsTimeout)  clearTimeout(this.statsTimeout);
    this.statsSocket?.destroy();
    this.statsSocket = null;

    this.destroyCore();
    this.updateHlsEventsHandlers("unregister");
    this.updateMediaElementEventHandlers("unregister");
    this.currentHlsInstance = undefined;
  };

  private createFragmentLoaderClass() {
    const { core } = this;
    const engine   = this;
    return class FragmentLoader extends FragmentLoaderBase {
      constructor(config: HlsConfig) { super(config, core); }
      static getEngine() { return engine; }
    };
  }

  private createPlaylistLoaderClass() {
    const engine = this;
    return class PlaylistLoader extends PlaylistLoaderBase {
      constructor(config: HlsConfig) {
        super(config);
        engine.initHlsEvents();
      }
    };
  }
}